import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { usePosts } from "../hooks/usePosts";
import { useAuth } from "../lib/auth";
import Post from "./Post";
import Speach from "./Speach";
import SuggestFollowBox from "./SuggestFollowBox";

const PostSkeleton = () => {
  return (
    <div className="bg-white dark:bg-gray-800 my-7 border border-gray-200 dark:border-gray-700 rounded-sm animate-pulse">
      
      {/* Media skeleton (image/video) */}
      <div className="w-full h-96 bg-gray-300 dark:bg-gray-600"></div>
      
      {/* Actions skeleton */}
      <div className="flex justify-between px-4 pt-4">
        <div className="flex space-x-2">
          <div className="h-6 w-6 bg-gray-300 dark:bg-gray-600 rounded"></div>
          <div className="h-6 w-6 bg-gray-300 dark:bg-gray-600 rounded"></div>
          <div className="h-6 w-6 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
        <div className="h-6 w-6 bg-gray-300 dark:bg-gray-600 rounded"></div>
      </div>
      
      {/* Likes and caption skeleton */}
      <div className="p-5 space-y-2">
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/6"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4"></div>
      </div>
      
      {/* Comment input skeleton */}
      <div className="flex items-center p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded flex-1 me-3"></div>
        <div className="h-6 w-12 bg-gray-300 dark:bg-gray-600 rounded"></div>
      </div>
    </div>
  );
};

type PostsProps = {
  tagName?: string;
};

const Posts: React.FC<PostsProps> = ({ tagName }) => {
  const observerRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePosts(tagName, "all", "following");


  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [handleObserver]);

  const allPosts = data?.pages?.flatMap((page: any) => page?.posts || []) || [];

  const suggestBoxAfterIndex = useMemo(
    () =>
      allPosts.length > 0
        ? Math.floor(Math.random() * allPosts.length)
        : -1,
    [allPosts.length]
  );

  if (isLoading) {
    return (
      <div>
        {Array.from({ length: 3 }).map((_, index) => (
          <PostSkeleton key={index} />
        ))}
      </div>
    );
  }

  const filteredPosts = allPosts.filter(
    (post: any) =>
      post?.id != null &&
      String(post.id) !== "null" &&
      String(post.id) !== "undefined"
  );

  return (
    <>
      <div>
        {filteredPosts.map((post: any, index: number) => (
          <React.Fragment key={post.postType === "text" ? `I${post.id}-speach` : `I${post.id}-post`}>
            {post.postType === "text" ? (
              <Speach
                id={String(post.id)}
                hashCode={post.hashCode}
                img={post.image}
                like={post.likes}
                isLikedByCurrentUser={post.isLikedByCurrentUser}
                video={post.video || null}
                caption={post.caption}
                userId={post.user}
                postType={post.postType}
                user={post.user}
                createdAt={post.createdAt}
                comments={post.comments}
                tags={post.tags}
                poll={post.poll}
              />
            ) : (
              <Post
                id={String(post.id)}
                hashCode={post.hashCode}
                img={post.image}
                like={post.likes}
                isLikedByCurrentUser={post.isLikedByCurrentUser}
                video={post.video || null}
                caption={post.caption}
                userId={post.user}
                showAllComments={false}
                postType={post.postType}
                user={post.user}
                createdAt={post.createdAt}
                comments={post.comments}
                tags={post.tags}
                poll={post.poll}
              />
            )}
            {isAuthenticated && suggestBoxAfterIndex === index && (
              <SuggestFollowBox />
            )}
          </React.Fragment>
        ))}

        <div ref={observerRef} className="h-4" />

        {isFetchingNextPage && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        )}
      </div>
    </>
  );
};

export default Posts;
